import { useMemo, useState } from "react";
import { Tag } from "../../../interfaces/Tag";
import { Rule } from "../../../interfaces/Rule";
import { CoObjectItem, CoObjectSummary } from "../../../interfaces/CoObject";
import { selectCellOrContentControl } from "../../../../office/ContentControl";
import { ActionGroup } from "../../../interfaces/ActionButton";
import { deleteTagFromCustomXml } from "../../../../utils/helpers/CustomXmlCoTagHelper";
import { formatMessage } from "../../../../utils/helpers/LanguageHelper";
import { getTags } from "../../../../utils/helpers/TagsHelper";
import { buildCoObjectSummaries } from "../../../../utils/helpers/ContentControlHelper";

export interface TagsPanelProps {
  documentId: string;
  coObjects?: CoObjectItem[];
  tags?: Tag[];
  updateTags?: (tags: Tag[]) => void;
  updateRules?: (rules: Rule[]) => void;
}

export interface TagsPanelResult {
  tags: Tag[];
  updateTags: (tags: Tag[]) => void;
  search: string;
  expanded: string[];
  showAddTagsModal: boolean;
  tagToEdit: Tag | null;
  setTagToEdit: (r: Tag | null) => void;
  setShowAddTagsModal: (show: boolean) => void;
  filteredTags: Tag[];
  handleAccordionToggle: (_: any, data: any) => void;
  handleObjectClick: (obj: CoObjectSummary) => void;
  handleFilter: (query: string) => void;
  actionGroups: ActionGroup[];
  deleteConfirm: boolean;
  tagToDelete: Tag | null;
  confirmDelete: () => Promise<void>;
  cancelDelete: () => void;
}

export function useTagsPanel(props: TagsPanelProps): TagsPanelResult {
  const { coObjects, tags = [], updateTags } = props;

  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string[]>([]);
  const [showAddTagsModal, setShowAddTagsModal] = useState(false);

  const [tagToEdit, setTagToEdit] = useState<Tag | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);

  const filteredTags = useMemo(() => {
    if (!search) return tags;
    const searchLower = search.toLowerCase();
    return tags.filter((t) => t.label.toLowerCase().includes(searchLower));
  }, [tags, search]);

  const handleAccordionToggle = (_: any, data: any) => {
    const openItems = Array.isArray(data.openItems) ? data.openItems : [data.openItems].filter(Boolean);
    setExpanded(openItems);
  };

  const handleObjectClick = (obj: CoObjectSummary) => {
    void selectCellOrContentControl(obj);
  };

  const handleFilter = (query: string) => {
    setSearch(query.toLowerCase());
    setExpanded((prev) => prev);
  };

  const confirmDelete = async () => {
    if (!tagToDelete) return;
    await deleteTagFromCustomXml(tagToDelete.id);
    const updatedTags = await getTags(coObjects);

    updateTags?.(updatedTags);
    setTagToDelete(null);
    setDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setTagToDelete(null);
    setDeleteConfirm(false);
  };

  const actionGroups: ActionGroup[] = [
    {
      key: "edit-actions",
      buttons: [
        {
          key: "edit",
          title: formatMessage("cof.base.edit"),
          icon: "k-icon k-i-edit",
          style: { color: "var(--color-blue)", cursor: "pointer" },
          onClick: (tagId: string) => {
            const t = tags.find((x) => x.id === tagId) || null;
            if (!t) return;
            void (async () => {
              const objectIds = t.objects.map((o) => o.id);
              const summaries = await buildCoObjectSummaries(objectIds);
              const enrichedTag: Tag = {
                ...t,
                objects: t.objects.map((o) => summaries[o.id] ?? o),
              };
              setTagToEdit(enrichedTag);
              setShowAddTagsModal(true);
            })();
          },
        },
        {
          key: "delete",
          title: formatMessage("cof.base.delete"),
          icon: "k-icon k-i-delete",
          style: { color: "var(--color-red)", cursor: "pointer" },
          onClick: (tagId: string) => {
            const t = tags.find((x) => x.id === tagId) ?? null;
            if (!t) return;
            setTagToDelete(t);
            setDeleteConfirm(true);
          },
        },
      ],
    },
  ];

  return {
    tags,
    updateTags: updateTags ?? (() => {}),
    search,
    expanded,
    showAddTagsModal,
    setShowAddTagsModal,
    tagToEdit,
    setTagToEdit,
    filteredTags,
    handleAccordionToggle,
    handleObjectClick,
    handleFilter,
    actionGroups,
    deleteConfirm,
    tagToDelete,
    confirmDelete,
    cancelDelete,
  };
}
